import React, { useCallback, useEffect, useState } from "react"
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap"
import { toast } from "react-toastify"
import API from "../../common/API"
import { buildRecordPath } from "../../common/recordIdentifier"

interface ParteRevision {
  id: number
  nombre?: string | null
  rol?: string | null
  identificador?: string | null
  cedulaMatchStatus: number
  cedulaMatchCandidates?: number | null
  cedulaMatchedAt?: string | null
  creado?: string | null
  cedulaMatchAttempts?: number
  idAudiencia: number
  nuc?: string | null
  tribunal?: string | null
  distritoJudicial?: string | null
  fechaDate?: string | null
  estadoRolAudiencia?: string | null
}

const STATUS_BADGE: Record<number, { label: string; bg: string; border: string; color: string }> = {
  0: { label: "Procesando", bg: "#f8fafc", border: "#94a3b8", color: "#334155" },
  1: { label: "Vinculado", bg: "#ecfdf5", border: "#16a34a", color: "#166534" },
  2: { label: "Ambiguo", bg: "#fffbeb", border: "#d97706", color: "#92400e" },
  3: { label: "Sin coincidencia", bg: "#fef2f2", border: "#dc2626", color: "#991b1b" },
  4: { label: "Manual", bg: "#eff6ff", border: "#2563eb", color: "#1e40af" },
  5: { label: "Empresa vinculada", bg: "#faf5ff", border: "#7c3aed", color: "#5b21b6" },
  6: { label: "Ambiguo (empresa)", bg: "#fffbeb", border: "#d97706", color: "#92400e" },
  7: { label: "Preasignado", bg: "#f0fdfa", border: "#0d9488", color: "#115e59" },
}

interface Candidato {
  tipo: string // "persona" | "empresa"
  identificador: string
  nombre?: string | null
  fechaNacimiento?: string | null
  sexo?: string | null
  lugarNacimiento?: string | null
}

const STATUS_OPTIONS = [
  { value: "Pending", label: "Pendientes (procesando)" },
  { value: "Ambiguous", label: "Ambiguos (varios candidatos)" },
  { value: "AmbiguousEntity", label: "Ambiguos (empresas)" },
  { value: "NotMatched", label: "Sin coincidencia" },
]

const AdminPartesRevisionComponent = () => {
  const [status, setStatus] = useState("Ambiguous")
  const [search, setSearch] = useState("")
  const [searchInput, setSearchInput] = useState("")
  const [rows, setRows] = useState<ParteRevision[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)

  // Modal de candidatos
  const [showModal, setShowModal] = useState(false)
  const [activeParte, setActiveParte] = useState<ParteRevision | null>(null)
  const [candidates, setCandidates] = useState<Candidato[]>([])
  const [normalizedName, setNormalizedName] = useState("")
  const [candidatesLoading, setCandidatesLoading] = useState(false)
  const [selected, setSelected] = useState<string>("")
  const [assigning, setAssigning] = useState(false)

  const fetchReview = useCallback(async (nextPage: number, nextStatus: string, nextSearch: string) => {
    setLoading(true)
    try {
      const qs = new URLSearchParams({ Page: String(nextPage), Quantity: "20" })
      if (nextSearch.trim()) {
        qs.set("Search", nextSearch.trim())
      } else {
        qs.set("Status", nextStatus)
      }
      const res = await API.getAction(`PartesCedulaMatch/Review?${qs.toString()}`)
      if (res.data.status === "ok") {
        setRows(res.data.response)
        setTotalPages(res.data.quantityOfPages || 1)
        setTotalRecords(res.data.quantityOfRecords || 0)
      }
    } catch {
      toast.error("No se pudo cargar la lista de partes")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchReview(0, status, "")
  }, [])

  const runSearch = async () => {
    setSearch(searchInput)
    setPage(0)
    await fetchReview(0, status, searchInput)
  }

  const clearSearch = async () => {
    setSearch("")
    setSearchInput("")
    setPage(0)
    await fetchReview(0, status, "")
  }

  const changeStatus = async (next: string) => {
    setStatus(next)
    setSearch("")
    setSearchInput("")
    setPage(0)
    await fetchReview(0, next, "")
  }

  const openCandidates = async (parte: ParteRevision) => {
    setActiveParte(parte)
    setSelected("")
    setCandidates([])
    setNormalizedName("")
    setShowModal(true)
    setCandidatesLoading(true)
    try {
      const res = await API.getAction(`PartesCedulaMatch/Candidates?ParteId=${parte.id}`)
      if (res.data.status === "ok") {
        setCandidates(res.data.response.candidatos || [])
        setNormalizedName(res.data.response.nombreNormalizado || "")
      }
    } catch {
      toast.error("No se pudieron cargar los candidatos")
    } finally {
      setCandidatesLoading(false)
    }
  }

  const assign = async () => {
    if (!activeParte || !selected) {
      toast.error("Selecciona un candidato")
      return
    }
    const candidato = candidates.find(c => `${c.tipo}:${c.identificador}` === selected)
    if (!candidato) return
    setAssigning(true)
    try {
      const res = await API.postAction("PartesCedulaMatch/Asignar", {
        parteId: activeParte.id,
        identificador: candidato.identificador,
        tipo: candidato.tipo,
      })
      if (res.data.status === "ok") {
        const fichas = res.data.response?.fichasProcesadas ?? 0
        toast.success(
          candidato.tipo === "empresa"
            ? "Empresa asignada al parte"
            : `Cédula asignada. ${fichas > 0 ? `Ficha(s) sincronizada(s): ${fichas}` : "Se sincronizará en breve."}`
        )
        setShowModal(false)
        await fetchReview(page, status, search)
      } else {
        toast.error(res.data.response || "No se pudo asignar")
      }
    } catch (ex: any) {
      toast.error(ex.message)
    } finally {
      setAssigning(false)
    }
  }

  const formatDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString("es-DO") : "—")

  return (
    <div className="p-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0"><i className="fas fa-user-check me-2" />Revisión de Cédulas</h5>
        <Button variant="outline-secondary" size="sm" onClick={() => fetchReview(page, status, search)} disabled={loading}>
          <i className="fas fa-sync-alt me-1" />Actualizar
        </Button>
      </div>

      <p className="text-muted small">
        Partes cuyo nombre no se pudo resolver automáticamente a una sola cédula. Revisa los candidatos y asigna la correcta;
        al asignar una persona se crea su ficha.
      </p>

      <Card className="border-0 shadow-sm mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end mb-3">
            <Col md={4}>
              <Form.Label className="small fw-semibold">Criterio</Form.Label>
              <Form.Select size="sm" value={status} onChange={e => changeStatus(e.target.value)} disabled={!!search}>
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Form.Select>
            </Col>
            <Col md={5}>
              <Form.Label className="small fw-semibold">Buscar por nombre o identificador</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  size="sm"
                  placeholder="Ej. HEIDI PINEDA o 22400160663"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") runSearch() }}
                />
                <Button variant="dark" size="sm" onClick={runSearch} disabled={loading || !searchInput.trim()}>
                  <i className="fas fa-search"></i>
                </Button>
                {search && (
                  <Button variant="outline-secondary" size="sm" onClick={clearSearch} disabled={loading}>
                    <i className="fas fa-times"></i>
                  </Button>
                )}
              </div>
            </Col>
            <Col md={3} className="text-md-end">
              <span className="text-muted small">{totalRecords.toLocaleString("es-DO")} registro(s)</span>
            </Col>
          </Row>
          {search && (
            <p className="small text-muted mb-2">
              Buscando <strong>&quot;{search}&quot;</strong> en todos los estados (ignora el criterio de arriba).
            </p>
          )}

          <div className="table-responsive">
            <Table hover size="sm" className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Rol</th>
                  <th>Estado</th>
                  <th>Registrado</th>
                  <th>NUC</th>
                  <th>Tribunal</th>
                  <th>Distrito</th>
                  <th>Fecha</th>
                  <th className="text-center">Candidatos</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} className="text-center py-4"><Spinner size="sm" className="me-2" />Cargando...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={10} className="text-center text-muted py-4">No hay partes para revisar con este criterio.</td></tr>
                ) : rows.map(p => {
                  const statusInfo = STATUS_BADGE[p.cedulaMatchStatus] ?? STATUS_BADGE[0]
                  return (
                  <tr key={p.id}>
                    <td>
                      {p.identificador ? (
                        <a href={buildRecordPath(p.identificador)} target="_blank" rel="noopener noreferrer">
                          <strong>{p.nombre || "—"}</strong>
                        </a>
                      ) : (
                        <strong>{p.nombre || "—"}</strong>
                      )}
                    </td>
                    <td>{p.rol || "—"}</td>
                    <td>
                      <span
                        className="badge fw-bold"
                        style={{ backgroundColor: statusInfo.bg, border: `1.5px solid ${statusInfo.border}`, color: statusInfo.color }}
                      >
                        {statusInfo.label}
                      </span>
                      {(p.cedulaMatchAttempts ?? 0) > 0 && (
                        <div className="text-muted" style={{ fontSize: "0.7rem" }}>{p.cedulaMatchAttempts} intento(s)</div>
                      )}
                    </td>
                    <td className="small">{formatDate(p.creado)}</td>
                    <td>{p.nuc || "—"}</td>
                    <td className="small">{p.tribunal || "—"}</td>
                    <td className="small">{p.distritoJudicial || "—"}</td>
                    <td className="small">{formatDate(p.fechaDate)}</td>
                    <td className="text-center">
                      {p.cedulaMatchCandidates ? <Badge bg="warning" text="dark">{p.cedulaMatchCandidates}</Badge> : <span className="text-muted">—</span>}
                    </td>
                    <td className="text-end">
                      {(p.cedulaMatchStatus === 2 || p.cedulaMatchStatus === 3 || p.cedulaMatchStatus === 6) ? (
                        <Button variant="primary" size="sm" onClick={() => openCandidates(p)}>
                          <i className="fas fa-search me-1" />Resolver
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                )})}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <Button variant="outline-secondary" size="sm" disabled={page === 0 || loading}
              onClick={async () => { const n = Math.max(0, page - 1); setPage(n); await fetchReview(n, status, search) }}>
              ‹ Anterior
            </Button>
            <small className="text-muted">Página {totalPages === 0 ? 0 : page + 1} de {totalPages}</small>
            <Button variant="outline-secondary" size="sm" disabled={loading || page >= totalPages - 1}
              onClick={async () => { const n = page + 1; setPage(n); await fetchReview(n, status, search) }}>
              Siguiente ›
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">
            Candidatos para: <span className="text-primary">{activeParte?.nombre}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {normalizedName && <p className="small text-muted mb-2">Nombre normalizado: <code>{normalizedName}</code></p>}
          {candidatesLoading ? (
            <div className="text-center py-4"><Spinner size="sm" className="me-2" />Buscando candidatos...</div>
          ) : candidates.length === 0 ? (
            <div className="alert alert-warning py-2 mb-0">
              No se encontraron candidatos por nombre exacto. Puede que el nombre tenga variaciones que el padrón no registra igual.
            </div>
          ) : (
            <Table hover size="sm" className="align-middle mb-0">
              <thead>
                <tr>
                  <th></th>
                  <th>Tipo</th>
                  <th>Identificador</th>
                  <th>Nombre</th>
                  <th>Nacimiento</th>
                  <th>Sexo</th>
                  <th>Lugar</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map(c => {
                  const key = `${c.tipo}:${c.identificador}`
                  return (
                    <tr key={key} onClick={() => setSelected(key)} style={{ cursor: "pointer" }}>
                      <td>
                        <Form.Check type="radio" name="candidato" checked={selected === key} onChange={() => setSelected(key)} />
                      </td>
                      <td>
                        {c.tipo === "empresa" ? (
                          <span
                            className="badge fw-bold"
                            style={{ backgroundColor: "#faf5ff", border: "1.5px solid #7c3aed", color: "#5b21b6" }}
                          >
                            Empresa
                          </span>
                        ) : (
                          <span
                            className="badge fw-bold"
                            style={{ backgroundColor: "#eff6ff", border: "1.5px solid #2563eb", color: "#1e40af" }}
                          >
                            Persona
                          </span>
                        )}
                      </td>
                      <td>
                        <a
                          href={buildRecordPath(c.identificador)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="Ver informe"
                        >
                          <code>{c.identificador}</code>
                          <i className="fa fa-arrow-up-right-from-square ms-1 small"></i>
                        </a>
                      </td>
                      <td>{c.nombre || "—"}</td>
                      <td className="small">{c.fechaNacimiento ? new Date(c.fechaNacimiento).toLocaleDateString("es-DO") : "—"}</td>
                      <td>{c.sexo || "—"}</td>
                      <td className="small">{c.lugarNacimiento || "—"}</td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowModal(false)}>Cancelar</Button>
          <Button variant="primary" size="sm" onClick={assign} disabled={assigning || !selected}>
            {assigning ? <><Spinner size="sm" className="me-1" />Asignando...</> : <><i className="fas fa-check me-1" />Asignar seleccionado</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default AdminPartesRevisionComponent
